import React, { useState, useEffect } from 'react';
import {
  Panel,
  PanelType,
  TextField,
  PrimaryButton,
  DefaultButton,
  Checkbox,
  Spinner,
  MessageBar,
  MessageBarType,
} from '@fluentui/react';
import useAxios from 'axios-hooks';
import Select from "react-select";
import { FixedSizeList as List } from "react-window";

const CustomMenuList = ({ children, ...props }) => {
  const height = 35;
  const itemCount = children.length;
  const totalHeight = Math.min(300, itemCount * height);

  return (
    <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
      {({ index, style }) => (
        <div
          style={{
            ...style,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            padding: "0 8px",
            boxSizing: "border-box",
          }}
          title={children[index]?.props?.children}
        >
          {children[index]}
        </div>
      )}
    </List>
  );
};

const AttributePanel = ({
  isOpen,
  onDismiss,
  attribute,
  cobrandList,
  isEditMode,
  onSaveSuccess,
}) => {
  const [formData, setFormData] = useState({
    attributeId: '',
    attributeName: '',
    attributeValue: '',
    valueFormatType: '',
    globalAttributeValue: '',
  });
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [errors, setErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [initialValue, setInitialValue] = useState('');

  const cobrandIdList = cobrandList.map(x => x.coBrandId).join(",");

  // Fetch available attributes for add mode
  const [{ data: optionsData, loading: loadingOptions }, getOptions] = useAxios(
    { url: 'internal/cobrand/attributes/options', method: "get" },
    { manual: true }
  );

  // Save attribute
  const [{ loading: saving }, saveAttribute] = useAxios(
    { url: 'internal/cobrand/attributes', method: "post" },
    { manual: true }
  );

  useEffect(() => {
    if (isOpen && !isEditMode) {
      // Fetch available attributes for add
      getOptions({
        method: "get",
        url: `internal/cobrand/attributes/options?cobrandIdList=${encodeURIComponent(cobrandIdList)}`,
      }).then((result) => {
        if (!result.isAxiosError && result.data) {
          setAvailableAttributes(result.data);
        }
      });
    }
  }, [isOpen, isEditMode, cobrandIdList]);

  useEffect(() => {
    if (isEditMode && attribute) {
      setFormData({
        attributeId: attribute.attributeId,
        attributeName: attribute.attributeName,
        attributeValue: attribute.attributeValue === null || attribute.attributeValue === undefined ? null : attribute.attributeValue,
        valueFormatType: attribute.valueFormatType,
        globalAttributeValue: attribute.globalAttributeValue || '',
      });
      setInitialValue(attribute.attributeValue === null || attribute.attributeValue === undefined ? null : attribute.attributeValue);
      setIsDirty(false);
    } else {
      setFormData({
        attributeId: '',
        attributeName: '',
        attributeValue: '',
        valueFormatType: '',
        globalAttributeValue: '',
      });
      setInitialValue('');
      setIsDirty(false);
    }
    setErrors({});
  }, [isEditMode, attribute, isOpen]);

  const validateField = (name, value) => {
    let error = '';

    if (name === 'attributeId' && !value) {
      error = 'Attribute is required';
    }

    if (name === 'attributeValue' && formData.valueFormatType !== 'N/A') {
      if (!value || value.trim() === '') {
        error = 'Attribute Value is required';
      } else {
        // Validate based on format type
        switch (formData.valueFormatType) {
          case 'Date':
            if (!isValidDate(value)) {
              error = 'Attribute Value must be a Date in mm/dd/yyyy format';
            }
            break;
          case 'Number':
          case 'Percent':
          case 'Money':
            if (isNaN(value) || value.trim() === '') {
              error = 'Attribute Value must be numeric';
            }
            break;
          default:
            break;
        }
      }
    }

    return error;
  };

  const isValidDate = (dateString) => {
    if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
      return false;
    }

    const parts = dateString.split("/");
    const day = parseInt(parts[1], 10);
    const month = parseInt(parts[0], 10);
    const year = parseInt(parts[2], 10);

    if (year < 1000 || year > 3000 || month === 0 || month > 12) {
      return false;
    }

    const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    if (year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) {
      monthLength[1] = 29;
    }

    return day > 0 && day <= monthLength[month - 1];
  };

  const handleAttributeSelect = (option) => {
    const selectedAttr = availableAttributes.find(a => a.attributeId === option.value);
    if (selectedAttr) {
      setFormData({
        attributeId: selectedAttr.attributeId,
        attributeName: selectedAttr.attributeName,
        attributeValue: '',
        valueFormatType: selectedAttr.valueFormatType,
        globalAttributeValue: selectedAttr.globalAttributeValue || '',
      });
      setErrors({});
    }
  };

  const handleInputChange = (event, newValue, fieldName) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: newValue,
    }));

    if (isEditMode && fieldName === 'attributeValue') {
      setIsDirty(newValue !== initialValue);
    }

    const error = validateField(fieldName, newValue);
    setErrors(prev => ({
      ...prev,
      [fieldName]: error,
    }));
  };

  const handleCheckboxChange = (event, checked) => {
    // When checked, send empty string; when unchecked, send null (matches Angular behavior)
    const newValue = checked ? '' : null;
    setFormData(prev => ({
      ...prev,
      attributeValue: newValue,
    }));
    
    if (isEditMode) {
      // Compare against initial value for dirty tracking
      const initialValueNormalized = initialValue === '' || initialValue === null || initialValue === undefined ? initialValue : String(initialValue);
      const newValueNormalized = newValue === '' || newValue === null ? newValue : String(newValue);
      setIsDirty(newValueNormalized !== initialValueNormalized);
    }
  };

  const handleSave = async () => {
    // Validate all fields
    const newErrors = {};
    if (!isEditMode) {
      const attrError = validateField('attributeId', formData.attributeId);
      if (attrError) newErrors.attributeId = attrError;
    }
    
    if (formData.valueFormatType !== 'N/A') {
      const valueError = validateField('attributeValue', formData.attributeValue);
      if (valueError) newErrors.attributeValue = valueError;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const result = await saveAttribute({
        method: "post",
        url: 'internal/cobrand/attributes',
        data: {
          attributeId: formData.attributeId,
          attributeValue: formData.attributeValue === null ? null : formData.attributeValue,
          cobrandIdList: cobrandIdList,
        },
      });

      if (!result.isAxiosError) {
        onSaveSuccess();
      }
    } catch (error) {
      console.error('Error saving attribute:', error);
    }
  };

  const renderValueInput = () => {
    if (formData.valueFormatType === 'N/A') {
      // For N/A type, checked means empty string, unchecked means null
      const isChecked = formData.attributeValue === '' || formData.attributeValue === true || formData.attributeValue === 'true';
      
      return (
        <Checkbox
          label="Enabled"
          checked={isChecked}
          onChange={handleCheckboxChange}
        />
      );
    }

    let prefix = '';
    let suffix = '';
    let placeholder = '';

    switch (formData.valueFormatType) {
      case 'Money':
        prefix = '$';
        break;
      case 'Percent':
        suffix = '%';
        break;
      case 'Date':
        placeholder = 'mm/dd/yyyy';
        break;
      case 'Number':
        placeholder = 'Number';
        break;
      case 'Text':
        placeholder = 'Text';
        break;
      default:
        break;
    }

    return (
      <div>
        <TextField
          label="Attribute Value"
          value={formData.attributeValue}
          onChange={(e, val) => handleInputChange(e, val, 'attributeValue')}
          errorMessage={errors.attributeValue}
          required
          prefix={prefix}
          suffix={suffix}
          placeholder={placeholder}
        />
      </div>
    );
  };

  const attributeDropdownOptions = availableAttributes.map(attr => ({
    value: attr.attributeId,
    label: `${attr.attributeId} - ${attr.attributeName}`,
  }));

  return (
    <Panel
      isOpen={isOpen}
      onDismiss={onDismiss}
      type={PanelType.medium}
      headerText={isEditMode ? "Edit Attribute" : "Add Attribute"}
      closeButtonAriaLabel="Close"
    >
      <div style={{ marginTop: 20 }}>
        {loadingOptions && <Spinner label="Loading attributes..." />}
        
        {!isEditMode && !loadingOptions && (
          <>
            <div className='panel-h' style={{ fontWeight: '500', marginBottom: 5 }}>Select Attribute</div>
            <Select
              components={{ MenuList: CustomMenuList }}
              options={attributeDropdownOptions}
              placeholder="Select an attribute"
              value={attributeDropdownOptions.find(option => option.value === formData.attributeId)}
              onChange={handleAttributeSelect}
              isSearchable
              styles={{
                container: base => ({ ...base, width: '100%' }),
                control: base => ({ 
                  ...base, 
                  border: errors.attributeId ? "1px solid rgb(164, 38, 44)" : "1px solid #aaaaaa" 
                }),
                menuList: base => ({ ...base, maxHeight: "300px", overflowY: "auto" }),
              }}
            />
            {errors.attributeId && (
              <div style={{ color: 'rgb(164, 38, 44)', fontSize: 12, marginTop: 5 }}>
                {errors.attributeId}
              </div>
            )}
          </>
        )}

        {isEditMode && (
          <div style={{ marginBottom: 15 }}>
            <strong>Attribute:</strong> {formData.attributeName}
          </div>
        )}

        {formData.attributeId && (
          <>
            <div style={{ marginTop: 15, marginBottom: 15 }}>
              {renderValueInput()}
            </div>

            {formData.globalAttributeValue && (
              <div style={{ marginBottom: 15, padding: 10, backgroundColor: '#f3f2f1' }}>
                <strong>Global Value:</strong> {formData.globalAttributeValue}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <PrimaryButton
                text={isEditMode ? "Update" : "Add"}
                onClick={handleSave}
                disabled={saving}
              />
              <DefaultButton text="Cancel" onClick={onDismiss} />
            </div>
          </>
        )}
      </div>
    </Panel>
  );
};

export default AttributePanel;
